import React, { type ReactNode } from 'react';
import { Text } from '../../atoms/text';
import Select from '../../atoms/inputs/select';
import Button from '../../atoms/buttons/button';
import { PiCaretRightBold } from 'react-icons/pi';
import { PaginationBar } from './pagination-bar';
import type { ComponentDensity } from 'src/modules/shared/types/component.type';

export interface TokenPaginationProps {
  pageSize: number;
  totalCount?: number;
  currentCount: number;
  onPageSizeChange: (newSize: number) => void;
  onNextPage: () => void;
  hasNextPage?: boolean;
  isLoading?: boolean;
  className?: string;
  density?: ComponentDensity;
  centerContent?: ReactNode;
  endAddon?: ReactNode;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100].map((size) => ({
  content: size.toString(),
  value: size.toString(),
}));

export const TokenPagination: React.FC<TokenPaginationProps> = ({
  pageSize,
  totalCount,
  currentCount,
  onPageSizeChange,
  onNextPage,
  hasNextPage,
  isLoading,
  className = '',
  density = 'default',
  centerContent,
  endAddon,
}) => {
  const textClasses = 'font-normal text-sm text-secondary-600';
  const isCompact = density === 'compact';
  const hideLabelClassName = isCompact ? 'hidden' : 'hidden xl:inline';

  const paginationControls = (
    <div className="flex shrink-0 items-center gap-3">
      <div className="flex shrink-0 items-center gap-2">
        <Text
          size="tiny"
          color="secondary"
          className={`${textClasses} ${hideLabelClassName}`}
        >
          {/* TODO: check when implementing i18n */}
          Traer
        </Text>
        <Select
          value={pageSize.toString()}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          options={PAGE_SIZE_OPTIONS}
          size="sm"
          className="!min-w-[60px] shrink-0"
        />
        <Text
          size="tiny"
          color="secondary"
          className={`${textClasses} ${hideLabelClassName}`}
        >
          {/* TODO: check when implementing i18n */}
          registros
        </Text>
      </div>

      <Button
        variant="primary"
        appearance="ghost"
        size={isCompact ? 'sm' : 'md'}
        onClick={onNextPage}
        disabled={!hasNextPage || isLoading}
        loading={isLoading}
        className="shrink-0"
      >
        {/* TODO: check when implementing i18n */}
        <Text size="tiny" weight="medium" className="text-sm">
          Cargar más
        </Text>
        <PiCaretRightBold className="w-4 h-4 mt-[3px]" />
      </Button>

      {endAddon}
    </div>
  );

  const countDisplay = (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <Text size="tiny" color="secondary" className={textClasses}>
        {currentCount} {/* TODO: check when implementing i18n */}
        registros
      </Text>
      {!isCompact && totalCount !== undefined && (
        <Text
          size="tiny"
          color="secondary"
          className={`${textClasses} hidden sm:inline`}
        >
          {/* TODO: check when implementing i18n */}
          de {totalCount}
        </Text>
      )}
    </div>
  );

  return (
    <PaginationBar
      start={countDisplay}
      center={centerContent}
      end={paginationControls}
      className={className}
    />
  );
};
